import { useEffect, useRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import gsap from "gsap";
import { useIsMobile } from "../../hooks/useIsMobile";

const Card3D = ({ isVisible }: { isVisible: boolean }) => {
    const isMobile = useIsMobile();
    const mountRef = useRef<HTMLDivElement | null>(null);
    const isUserInteracting = useRef(false);

    useEffect(() => {
        // Scene setup
        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(75, 400 / 250, 0.1, 1000);
        const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        renderer.setSize(700, 500); // Increased div size

        if (mountRef.current) {
            mountRef.current.appendChild(renderer.domElement);
        }

        // Load card textures
        // const textureLoader = new THREE.TextureLoader();
        // const frontTexture = textureLoader.load("/front-2.svg");
        // frontTexture.generateMipmaps = false;
        const textureLoader = new THREE.TextureLoader();
        const frontTexture = textureLoader.load("/front-2.svg");
        frontTexture.minFilter = THREE.LinearFilter; // Use LinearFilter for sharpness
        frontTexture.magFilter = THREE.LinearFilter;
        frontTexture.anisotropy = renderer.capabilities.getMaxAnisotropy(); // Max anisotropy for clarity

        const backTexture = textureLoader.load("/back.jpg");
        backTexture.generateMipmaps = false;

        // Create card geometry
        const geometry = new RoundedBoxGeometry(5, 3, 0.05, 10, 0.9);
        const materials = [
            new THREE.MeshStandardMaterial({ color: 0xcccccc, roughness: 0.3, metalness: 0.1 }),
            new THREE.MeshStandardMaterial({ color: 0xcccccc, roughness: 0.3, metalness: 0.1 }),
            new THREE.MeshStandardMaterial({ color: 0xcccccc, roughness: 0.3, metalness: 0.1 }),
            new THREE.MeshStandardMaterial({ color: 0xcccccc, roughness: 0.3, metalness: 0.1 }),
            new THREE.MeshBasicMaterial({ map: frontTexture }), // Back side
            new THREE.MeshBasicMaterial({ map: backTexture }),
        ];

        const card = new THREE.Mesh(geometry, materials);
        scene.add(card);
        card.position.set(0, 0, 0);

        // Camera position
        camera.position.set(0, 0, isMobile ? 5.5 : 4.5); // Adjusted zoom

        // Add lighting
        const light = new THREE.AmbientLight(0xffffff, 1);
        scene.add(light);

        // Controls
        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controls.dampingFactor = 0.1;
        controls.enableZoom = false;

        controls.addEventListener("start", () => {
            rotationTween.pause();
            isUserInteracting.current = true;
        });
        controls.addEventListener("end", () => {
            rotationTween.resume();
            isUserInteracting.current = false;
        });

        // Rotate once on load
        gsap.to(card.rotation, {
            y: Math.PI * 2,
            duration: 2,
            ease: "power2.out",
            onComplete: () => {
                card.rotation.y = 0;
            },
        });

        let rotationTween: gsap.core.Tween;
        // Animation loop
        if (isVisible) {
            rotationTween = gsap.to(card.rotation, {
                y: Math.PI * 2,
                duration: 10,
                ease: "linear",
                paused: false,
                onComplete: () => {
                    card.rotation.y = 0;
                },
            });
        }

        const animate = () => {
            requestAnimationFrame(animate);
            controls.update();
            renderer.render(scene, camera);
        };

        animate();


        // Cleanup
        return () => {
            const mount = mountRef.current;
            if (mount) {
                mount.removeChild(renderer.domElement);
            }
        };
    }, [isMobile, isVisible]);

    return <div ref={mountRef} style={{
        width: '100%', // Ensure it does not exceed the screen width
        maxWidth: isMobile ? '300px' : '400px', // Limit width to the card size
        height: isMobile ? '350px' : '500px',
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden", // Prevent extra horizontal space
    }} />;
};

export default Card3D;
